import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  AlertTriangle,
  FileText,
  MapPin,
  Settings2,
  Upload,
  CheckCircle,
  FolderPlus,
  ExternalLink,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { api } from '../core/apiClient';
import apiClient from '../core/apiClient';
import TenderAnalysisPanel from './crm/TenderAnalysisPanel';
import TenderEvaluationPanel from './crm/TenderEvaluationPanel';
import { MoneyInput } from '../components/ui/money-input';
import { useCompositeCodes, compositeCodeLabel } from '../hooks/useCompositeCodes';
import CreateClientModal from '../components/CreateClientModal';

// ---- Mapa de dies en català ----
const DIA_MAP: Record<number, string> = {
  0: 'Diumenge',
  1: 'Dilluns',
  2: 'Dimarts',
  3: 'Dimecres',
  4: 'Dijous',
  5: 'Divendres',
  6: 'Dissabte',
};


interface ClientOption { id: string; name: string; cif?: string; }
interface CompanyUser { id: string; firstName: string; lastName: string; }

interface FormData {
  noticeDate: string;
  deadlineDate: string;
  deadlineTime: string;
  location: string;
  poblacio: string;
  totalAmount: string;
  adjudicationAmount: string;
  tenderType: string;
  compositeCodeId: string; // Tipus de producte (id de composite_codes)
  compositeSubfamilyId: string; // Subfamília (id de composite_subfamilies)
  observations: string;
  result: string;
  status: string;
  clientId: string;
  responsable: string;
}

const initialFormData: FormData = {
  noticeDate: '',
  deadlineDate: '',
  deadlineTime: '',
  location: '',
  poblacio: '',
  totalAmount: '',
  adjudicationAmount: '',
  tenderType: '',
  compositeCodeId: '',
  compositeSubfamilyId: '',
  observations: '',
  result: '',
  status: 'NO_PRESENTAT',
  clientId: '',
  responsable: '',
};

function getDayName(dateStr: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return '—';
  return DIA_MAP[d.getDay()] ?? '—';
}

// Converteix "DD-MM-YYYY" o "DD/MM/YYYY" → "YYYY-MM-DD"
function parseDatePcsp(str: string): string {
  const m = str.match(/(\d{2})[\/\-](\d{2})[\/\-](\d{4})/);
  if (!m) return '';
  return `${m[3]}-${m[2]}-${m[1]}`;
}

// Extreu hora "HH:MM" d'un string tipus "21/07/2026 a las 23:59"
function parseTimePcsp(str: string): string {
  const m = str.match(/(\d{2}):(\d{2})$/);
  return m ? `${m[1]}:${m[2]}` : '';
}

export default function LicitacioForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const compositeCodes = useCompositeCodes();
  const [compositeSubfamilies, setCompositeSubfamilies] = useState<{ id: number; code: string; name: string; compositeCodeId: number; isActive: number | boolean }[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extractedFields, setExtractedFields] = useState<string[]>([]);
  const [extractedEntitat, setExtractedEntitat] = useState('');
  // Valors originals extrets automàticament (per detectar correccions manuals)
  const [extractedValues, setExtractedValues] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // "Organ contractant" — autocomplete de Clients (substitueix el catàleg
  // aïllat TenderConstructor), mateix patró que ExpedientComercialForm.tsx.
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [clientOpen, setClientOpen] = useState(false);
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);

  // Crear Expedient Comercial des de la licitació
  const [ecId, setEcId] = useState<string | null>(null);
  const [creatingEc, setCreatingEc] = useState(false);

  useEffect(() => {
    api.get<{ success: boolean; data: ClientOption[] }>('/clients', { limit: 500, isActive: 1 })
      .then((res) => setClients(res.data ?? []))
      .catch(() => setClients([]));
  }, []);

  useEffect(() => {
    api.get<{ success: boolean; data: CompanyUser[] }>('/auth/users')
      .then((res) => setCompanyUsers(res.data ?? []))
      .catch(() => setCompanyUsers([]));
  }, []);

  useEffect(() => {
    api.get<{ success: boolean; data: typeof compositeSubfamilies }>('/catalog/composite-subfamilies')
      .then((res) => setCompositeSubfamilies((res.data ?? []).filter((s) => Number(s.isActive) === 1)))
      .catch(() => setCompositeSubfamilies([]));
  }, []);

  // Carregar licitació si editem
  useEffect(() => {
    if (isEditing && id) {
      const fetchTender = async () => {
        setLoading(true);
        try {
          const res = await api.get<{ success: boolean; data: any }>(`/tenders/${id}`);
          const t = res.data;

          // Separar deadline en data + hora
          let deadlineDate = '';
          let deadlineTime = '';
          if (t.deadline) {
            const d = new Date(t.deadline);
            if (!isNaN(d.getTime())) {
              deadlineDate = d.toISOString().slice(0, 10);
              deadlineTime = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
            }
          }

          // noticeDate pot ser "YYYY-MM-DD" o datetime
          let noticeDate = '';
          if (t.noticeDate) {
            noticeDate = String(t.noticeDate).slice(0, 10);
          }

          setFormData({
            noticeDate,
            deadlineDate,
            deadlineTime,
            location: t.location || '',
            poblacio: t.poblacio || '',
            totalAmount: t.totalAmount != null ? String(t.totalAmount) : '',
            adjudicationAmount: t.adjudicationAmount != null ? String(t.adjudicationAmount) : '',
            tenderType: t.tenderType || '',
            compositeCodeId: t.compositeCodeId != null ? String(t.compositeCodeId) : '',
            compositeSubfamilyId: t.compositeSubfamilyId != null ? String(t.compositeSubfamilyId) : '',
            observations: t.observations || '',
            result: t.result || '',
            status: t.status || 'NO_PRESENTAT',
            clientId: t.clientId || '',
            responsable: t.responsable || '',
          });
          setClientSearch(t.client?.name ?? '');
          setEcId(t.ecId ?? null);
        } catch (err) {
          console.error('[v0] Error fetching tender:', err);
          setError('Error en carregar la licitació');
        } finally {
          setLoading(false);
        }
      };
      fetchTender();
    }
  }, [id, isEditing]);

  const handleExtractFromPdf = async (file: File) => {
    setExtracting(true);
    setExtractedFields([]);
    setExtractedEntitat('');
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const res = await apiClient.post(
        '/tenders/extract-preview',
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const d: Record<string, string | number> = res.data?.data ?? {};
      if (!d || Object.keys(d).length === 0) return;

      // Desa els valors originals extrets per detectar correccions en el submit
      const rawExtracted: Record<string, string> = {};
      if (d.objecte_contracte) rawExtracted.objecte_contracte = String(d.objecte_contracte);
      if (d.lloc_execucio) rawExtracted.lloc_execucio = String(d.lloc_execucio);
      if (d.pressupost_base) rawExtracted.pressupost_base = String(d.pressupost_base);
      setExtractedValues(rawExtracted);

      const updates: Partial<FormData> = {};
      const filled: string[] = [];

      if (d.objecte_contracte) {
        updates.location = String(d.objecte_contracte);
        filled.push('Objecte');
      }
      if (d.lloc_execucio) {
        updates.poblacio = String(d.lloc_execucio);
        filled.push('Població');
      }
      if (d.pressupost_base) {
        updates.totalAmount = String(d.pressupost_base);
        filled.push('Import Total');
      }
      if (d.data_publicacio) {
        const date = parseDatePcsp(String(d.data_publicacio));
        if (date) { updates.noticeDate = date; filled.push('Data Avís'); }
      }
      if (d.termini_presentacio_ofertes) {
        const raw = String(d.termini_presentacio_ofertes);
        const date = parseDatePcsp(raw);
        const time = parseTimePcsp(raw);
        if (date) { updates.deadlineDate = date; filled.push('Venciment'); }
        if (time) { updates.deadlineTime = time; filled.push('Hora Venciment'); }
      }

      // Construeix el camp Observacions amb la informació administrativa
      const obsLines: string[] = [];
      if (d.expedient)            obsLines.push(`Expedient: ${d.expedient}`);
      if (d.procediment)          obsLines.push(`Procediment: ${d.procediment}`);
      if (d.codi_cpv)             obsLines.push(`CPV: ${d.codi_cpv}`);
      if (d.termini_execucio)     obsLines.push(`Termini execució: ${d.termini_execucio}`);
      if (d.observacions_execucio) obsLines.push(`Obs. execució: ${d.observacions_execucio}`);
      if (obsLines.length > 0) {
        updates.observations = obsLines.join('\n');
        filled.push('Observacions');
      }

      if (d.entitat_convocant) {
        setExtractedEntitat(String(d.entitat_convocant));
      }

      setFormData(prev => ({ ...prev, ...updates }));
      setExtractedFields(filled);
    } catch (err) {
      console.error('[LicitacioForm] Error extract-preview:', err);
    } finally {
      setExtracting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClientCreated = (client: ClientOption) => {
    setClients((prev) => [client, ...prev]);
    setFormData((prev) => ({ ...prev, clientId: client.id }));
    setClientSearch(client.name);
    setClientOpen(false);
    setShowCreateClient(false);
  };

  const handleCreateEc = async () => {
    if (!id) return;
    if (!formData.clientId) { alert("Cal seleccionar l'Òrgan contractant (client) abans de crear l'Expedient"); return; }
    setCreatingEc(true);
    try {
      const res = await api.post<{ success: boolean; data: { id: string } }>(`/tenders/${id}/create-ec`);
      setEcId(res.data.id);
      navigate(`/crm/expedients-comercials/${res.data.id}/edit`);
    } catch (err: any) {
      alert(err?.response?.data?.message ?? "Error creant l'Expedient Comercial");
    } finally {
      setCreatingEc(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Construir deadline ISO combinant data + hora
      let deadlineIso: string | undefined;
      if (formData.deadlineDate) {
        const time = formData.deadlineTime || '00:00';
        deadlineIso = `${formData.deadlineDate}T${time}:00`;
      }

      const payload: Record<string, any> = {
        noticeDate: formData.noticeDate?.trim() || undefined,
        deadline: deadlineIso,
        location: formData.location?.trim() || undefined,
        poblacio: formData.poblacio?.trim() || undefined,
        totalAmount: formData.totalAmount !== '' ? parseFloat(formData.totalAmount) : undefined,
        adjudicationAmount: formData.adjudicationAmount !== '' ? parseFloat(formData.adjudicationAmount) : undefined,
        tenderType: formData.tenderType?.trim() || undefined,
        // null si es buida: així es pot treure el tipus de producte d'una licitació
        compositeCodeId: formData.compositeCodeId ? Number(formData.compositeCodeId) : null,
        compositeSubfamilyId: formData.compositeSubfamilyId ? Number(formData.compositeSubfamilyId) : null,
        observations: formData.observations?.trim() || undefined,
        result: formData.result?.trim() || undefined,
        status: formData.status || undefined,
        clientId: formData.clientId?.trim() || undefined,
        responsable: formData.responsable?.trim() || null,
      };

      // Netejar undefined
      Object.keys(payload).forEach((k) => {
        if (payload[k] === undefined) delete payload[k];
      });

      if (isEditing && id) {
        await api.put(`/tenders/${id}`, payload);
      } else {
        await api.post('/tenders', payload);
      }

      // Envia correccions manuals al sistema d'aprenentatge (fire-and-forget, no bloqueja)
      if (!isEditing && Object.keys(extractedValues).length > 0) {
        const feedbackItems: { fieldName: string; extractedValue: string; correctedValue: string }[] = [];
        if (extractedValues.objecte_contracte && formData.location !== extractedValues.objecte_contracte) {
          feedbackItems.push({ fieldName: 'objecte_contracte', extractedValue: extractedValues.objecte_contracte, correctedValue: formData.location });
        }
        if (extractedValues.lloc_execucio && formData.poblacio !== extractedValues.lloc_execucio) {
          feedbackItems.push({ fieldName: 'lloc_execucio', extractedValue: extractedValues.lloc_execucio, correctedValue: formData.poblacio });
        }
        if (extractedValues.pressupost_base && formData.totalAmount !== extractedValues.pressupost_base) {
          feedbackItems.push({ fieldName: 'pressupost_base', extractedValue: extractedValues.pressupost_base, correctedValue: formData.totalAmount });
        }
        if (feedbackItems.length > 0) {
          apiClient.post('/tenders/extraction-feedback', { items: feedbackItems }).catch(() => {});
        }
      }

      sessionStorage.setItem('erp:crm:activeTab', JSON.stringify('licitacions'));
      navigate('/crm');
    } catch (err: any) {
      console.error('[v0] Error saving tender:', err);
      setError(err.response?.data?.error?.message || 'Error en guardar la licitació');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const dayName = getDayName(formData.deadlineDate);

  return (
    <div className="space-y-6">
      {/* Capçalera sticky amb botons d'acció sempre visibles */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b flex items-center justify-between gap-4 py-3 -mx-4 px-4 md:-mx-6 md:px-6">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => { sessionStorage.setItem('erp:crm:activeTab', JSON.stringify('licitacions')); navigate('/crm'); }}
            className="p-2 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-foreground truncate">
              {isEditing ? 'Editar Licitació' : 'Nova Licitació'}
            </h1>
            <p className="text-xs text-muted-foreground">
              {isEditing ? 'Modificar les dades de la licitació' : 'Crear una nova licitació al sistema'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {isEditing && (
            ecId ? (
              <button
                type="button"
                onClick={() => navigate(`/crm/expedients-comercials/${ecId}/edit`)}
                className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-xs text-gray-700 hover:bg-gray-50"
              >
                <ExternalLink className="h-4 w-4" />
                Veure Expedient
              </button>
            ) : (
              <button
                type="button"
                onClick={handleCreateEc}
                disabled={creatingEc}
                className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                title="Crea l'Expedient Comercial a partir d'aquesta licitació"
              >
                {creatingEc ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderPlus className="h-4 w-4" />}
                {creatingEc ? 'Creant...' : 'Crear Expedient'}
              </button>
            )
          )}
          <button
            type="button"
            onClick={() => { sessionStorage.setItem('erp:crm:activeTab', JSON.stringify('licitacions')); navigate('/crm'); }}
            className="px-4 py-2 border border-gray-200 rounded-lg text-xs text-gray-500 hover:bg-gray-50"
          >
            Cancel·lar
          </button>
          <button
            type="submit"
            form="licitacio-form"
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-[#2563EB] text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {saving ? 'Desant...' : 'Desa'}
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Zona de càrrega del Documento de Pliegos — només en mode creació */}
      {!isEditing && (
        <Card className="border-dashed border-2 border-primary/30 bg-primary/5">
          <CardContent className="py-5">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">Auto-omplir des del Documento de Pliegos</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Puja el PDF de la PCSP i els camps s'ompliran automàticament: objecte, dates, import, expedient...
                </p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {extracting && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={extracting}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  <Upload className="h-4 w-4" />
                  {extracting ? 'Processant...' : 'Pujar PDF'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleExtractFromPdf(file);
                  }}
                />
              </div>
            </div>
            {extractedFields.length > 0 && (
              <div className="mt-3 flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-green-800">
                  <span className="font-semibold">{extractedFields.length} camps auto-omplerts:</span>{' '}
                  {extractedFields.join(', ')}.
                  {extractedEntitat && (
                    <span className="block mt-1">
                      Organ contractant detectat: <strong>{extractedEntitat}</strong> —{' '}
                      <button type="button" className="underline hover:no-underline" onClick={() => setClientSearch(extractedEntitat)}>
                        cerca'l al camp inferior
                      </button>.
                    </span>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className={`grid grid-cols-1 gap-6 items-start ${isEditing ? 'lg:grid-cols-2' : ''}`}>
      <form id="licitacio-form" onSubmit={handleSubmit}>
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100 text-sm font-semibold text-gray-900">
              <FileText size={16} className="text-blue-600" />
              Dates i Terminis
            </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Data Avís</label>
              <input
                type="date"
                name="noticeDate"
                value={formData.noticeDate}
                onChange={handleChange}
                className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Venciment (data)</label>
              <input
                type="date"
                name="deadlineDate"
                value={formData.deadlineDate}
                onChange={handleChange}
                className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Hora Venciment</label>
              <input
                type="time"
                name="deadlineTime"
                value={formData.deadlineTime}
                onChange={handleChange}
                className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Dia de la Setmana</label>
              <input
                type="text"
                value={dayName}
                readOnly
                className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Estat</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="CANDIDATA">Candidat (per revisar)</option>
                <option value="NO_PRESENTAT">En estudi</option>
                <option value="PRESENTAT_PENDENT">Licitat</option>
                <option value="PRESENTAT_PERDUT">Desestimat</option>
                <option value="GUANYAT">Proposat</option>
                <option value="DESCARTADA">Descartat</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Responsable</label>
              <select
                name="responsable"
                value={formData.responsable}
                onChange={handleChange}
                className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">— Selecciona —</option>
                {formData.responsable && !companyUsers.some((u) => `${u.firstName} ${u.lastName}` === formData.responsable) && (
                  <option value={formData.responsable}>{formData.responsable}</option>
                )}
                {companyUsers.map((u) => (
                  <option key={u.id} value={`${u.firstName} ${u.lastName}`}>{u.firstName} {u.lastName}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Dades de la Licitació */}
          <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100 text-sm font-semibold text-gray-900">
              <MapPin size={16} className="text-amber-600" />
              Dades de la Licitació
            </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-muted-foreground mb-1">Objecte</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Població</label>
              <input
                type="text"
                name="poblacio"
                value={formData.poblacio}
                onChange={handleChange}
                className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Import Total (€)</label>
              <MoneyInput
                value={formData.totalAmount}
                onValueChange={(v) => setFormData((prev) => ({ ...prev, totalAmount: v }))}
                className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Import Adjudicació (€)</label>
              <MoneyInput
                value={formData.adjudicationAmount}
                onValueChange={(v) => setFormData((prev) => ({ ...prev, adjudicationAmount: v }))}
                placeholder="Es coneix en resoldre la licitació"
                className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Ràtio Adjudicació/Total</label>
              <input
                type="text"
                readOnly
                value={formData.adjudicationAmount !== '' && parseFloat(formData.totalAmount) > 0
                  ? `${(parseFloat(formData.adjudicationAmount) / parseFloat(formData.totalAmount) * 100).toLocaleString('ca-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`
                  : '—'}
                className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Tipus de producte</label>
              <select
                name="compositeCodeId"
                value={formData.compositeCodeId}
                onChange={(e) => setFormData((f) => ({ ...f, compositeCodeId: e.target.value, compositeSubfamilyId: '' }))}
                className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— Selecciona —</option>
                {compositeCodes.optionsFor(formData.compositeCodeId).map((cc) => (
                  <option key={cc.id} value={String(cc.id)}>{compositeCodeLabel(cc)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Subfamília</label>
              <select
                name="compositeSubfamilyId"
                value={formData.compositeSubfamilyId}
                disabled={!formData.compositeCodeId}
                onChange={handleChange}
                className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="">— Selecciona —</option>
                {compositeSubfamilies
                  .filter((s) => !formData.compositeCodeId || String(s.compositeCodeId) === formData.compositeCodeId)
                  .map((s) => (
                    <option key={s.id} value={String(s.id)}>{s.code} - {s.name}</option>
                  ))}
              </select>
            </div>
            <div className="relative">
              <label className="block text-xs font-medium text-muted-foreground mb-1">Organ contractant</label>
              <div className="relative">
                <input
                  type="text"
                  value={clientSearch}
                  onChange={(e) => {
                    setClientSearch(e.target.value);
                    setFormData((prev) => ({ ...prev, clientId: '' }));
                    setClientOpen(true);
                  }}
                  onFocus={() => setClientOpen(true)}
                  onBlur={() => setTimeout(() => setClientOpen(false), 150)}
                  placeholder="Cerca per nom o CIF..."
                  className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {clientOpen && (() => {
                  const q = clientSearch.trim().toLowerCase();
                  const matches = q.length === 0
                    ? clients.slice(0, 10)
                    : clients.filter((c) => c.name.toLowerCase().includes(q) || (c.cif ?? '').toLowerCase().includes(q)).slice(0, 10);
                  return (
                    <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg max-h-56 overflow-y-auto">
                      {matches.map((c) => (
                        <li key={c.id}
                          onMouseDown={() => { setFormData((prev) => ({ ...prev, clientId: c.id })); setClientSearch(c.name); setClientOpen(false); }}
                          className="px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer">
                          {c.name}{c.cif ? <span className="text-muted-foreground"> — {c.cif}</span> : null}
                        </li>
                      ))}
                      <li
                        onMouseDown={() => setShowCreateClient(true)}
                        className="px-3 py-2 text-sm font-medium text-primary hover:bg-gray-50 cursor-pointer border-t">
                        + Crear client nou{clientSearch.trim() ? ` "${clientSearch.trim()}"` : ''}
                      </li>
                    </ul>
                  );
                })()}
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-muted-foreground mb-1">Observacions</label>
              <textarea
                name="observations"
                value={formData.observations}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-muted-foreground mb-1">Resultat</label>
              <textarea
                name="result"
                value={formData.result}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>
          </div>
        </div>

      </form>

      {isEditing && id && (
        <div className="space-y-6">
          {/* Avaluació segons criteris de valoració configurats (oberta) */}
          <TenderEvaluationPanel tenderId={id} />
          {/* Lectura de licitacions: documents oficials i informe d'anàlisi (tancats) */}
          <TenderAnalysisPanel tenderId={id} />
        </div>
      )}
      </div>

      <CreateClientModal
        open={showCreateClient}
        initialName={extractedEntitat || clientSearch}
        onClose={() => setShowCreateClient(false)}
        onCreated={handleClientCreated}
      />
    </div>
  );
}
